/**
 * API Response Utilities
 * Standardized handling of Lambda ApiResponse<T> format across all API calls
 *
 * Lambda Response Format (from lambda-config.md):
 * Success: { success: true, data: T, meta?: object }
 * Error: { success: false, error: string }
 */

import type { ApiResponse, SuccessResponse, ErrorResponse } from '../types/backend';
import apiClient from '@/services/api-client';
import { logger } from '@/services/logger.service';

/**
 * API Error class for standardized error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: any,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Type guard to check if response is a success response
 */
export function isSuccessResponse<T>(response: ApiResponse<T>): response is SuccessResponse<T> {
  return response.success === true && response.data !== undefined;
}

/**
 * Type guard to check if response is an error response
 */
export function isErrorResponse(response: ApiResponse<any>): response is ErrorResponse {
  return response.success === false && response.error !== undefined;
}

/**
 * Unwrap ApiResponse<T> wrapper
 * - If success: return data
 * - If error: throw ApiError
 * - If malformed: throw ApiError
 */
export function unwrapApiResponse<T>(response: ApiResponse<T>): T {
  if (isSuccessResponse(response)) {
    return response.data as T;
  }

  if (isErrorResponse(response)) {
    // error is a string in the Lambda envelope format
    const errorMessage = typeof response.error === 'string'
      ? response.error
      : (response.error as any)?.message || 'Unknown error';
    throw new ApiError(
      errorMessage,
      undefined,
      undefined,
      'API_ERROR'
    );
  }

  // Malformed response
  throw new ApiError(
    'Invalid API response format',
    500,
    { response },
    'INVALID_RESPONSE_FORMAT'
  );
}

/**
 * Make a GET request and unwrap the ApiResponse<T>
 * @param url - API endpoint (e.g., '/listings' or '/listings/123')
 * @param options - Optional request options
 * @returns Unwrapped data of type T
 * @throws ApiError if request fails or response indicates error
 */
export async function apiGet<T>(url: string, options?: any): Promise<T> {
  try {
    const response = await apiClient.get<ApiResponse<T>>(url, options);
    return unwrapApiResponse(response);
  } catch (error: any) {
    // If axios error, check if response has ApiResponse format
    if (error.response?.data) {
      const responseData = error.response.data as ApiResponse<T>;
      if (isErrorResponse(responseData)) {
        // error is a string in the Lambda envelope format
        const errMsg = typeof responseData.error === 'string'
          ? responseData.error
          : (responseData.error as any)?.message || 'API error';
        throw new ApiError(
          errMsg,
          error.response.status,
          undefined,
          'API_ERROR'
        );
      }
    }

    // Generic error
    throw new ApiError(
      error.message || 'API request failed',
      error.response?.status,
      error.response?.data,
      error.code
    );
  }
}

/**
 * Make a POST request and unwrap the ApiResponse<T>
 * @param url - API endpoint
 * @param data - Request body
 * @param options - Optional request options
 * @returns Unwrapped data of type T
 * @throws ApiError if request fails or response indicates error
 */
export async function apiPost<T>(url: string, data?: any, options?: any): Promise<T> {
  try {
    const response = await apiClient.post<ApiResponse<T>>(url, data, options);
    return unwrapApiResponse(response);
  } catch (error: any) {
    if (error.response?.data) {
      const responseData = error.response.data as ApiResponse<T>;
      if (isErrorResponse(responseData)) {
        // error is a string in the Lambda envelope format
        const errMsg = typeof responseData.error === 'string'
          ? responseData.error
          : (responseData.error as any)?.message || 'API error';
        throw new ApiError(
          errMsg,
          error.response.status,
          undefined,
          'API_ERROR'
        );
      }
    }

    throw new ApiError(
      error.message || 'API request failed',
      error.response?.status,
      error.response?.data,
      error.code
    );
  }
}

/**
 * Make a PUT request and unwrap the ApiResponse<T>
 */
export async function apiPut<T>(url: string, data?: any, options?: any): Promise<T> {
  try {
    const response = await apiClient.put<ApiResponse<T>>(url, data, options);
    return unwrapApiResponse(response);
  } catch (error: any) {
    if (error.response?.data) {
      const responseData = error.response.data as ApiResponse<T>;
      if (isErrorResponse(responseData)) {
        // error is a string in the Lambda envelope format
        const errMsg = typeof responseData.error === 'string'
          ? responseData.error
          : (responseData.error as any)?.message || 'API error';
        throw new ApiError(
          errMsg,
          error.response.status,
          undefined,
          'API_ERROR'
        );
      }
    }

    throw new ApiError(
      error.message || 'API request failed',
      error.response?.status,
      error.response?.data,
      error.code
    );
  }
}

/**
 * Make a DELETE request and unwrap the ApiResponse<T>
 */
export async function apiDelete<T>(url: string, options?: any): Promise<T> {
  try {
    const response = await apiClient.delete<ApiResponse<T>>(url, options);
    return unwrapApiResponse(response);
  } catch (error: any) {
    if (error.response?.data) {
      const responseData = error.response.data as ApiResponse<T>;
      if (isErrorResponse(responseData)) {
        // error is a string in the Lambda envelope format
        const errMsg = typeof responseData.error === 'string'
          ? responseData.error
          : (responseData.error as any)?.message || 'API error';
        throw new ApiError(
          errMsg,
          error.response.status,
          undefined,
          'API_ERROR'
        );
      }
    }

    throw new ApiError(
      error.message || 'API request failed',
      error.response?.status,
      error.response?.data,
      error.code
    );
  }
}

/**
 * Make a PATCH request and unwrap the ApiResponse<T>
 */
export async function apiPatch<T>(url: string, data?: any, options?: any): Promise<T> {
  try {
    const response = await apiClient.patch<ApiResponse<T>>(url, data, options);
    return unwrapApiResponse(response);
  } catch (error: any) {
    if (error.response?.data) {
      const responseData = error.response.data as ApiResponse<T>;
      if (isErrorResponse(responseData)) {
        // error is a string in the Lambda envelope format
        const errMsg = typeof responseData.error === 'string'
          ? responseData.error
          : (responseData.error as any)?.message || 'API error';
        throw new ApiError(
          errMsg,
          error.response.status,
          undefined,
          'API_ERROR'
        );
      }
    }

    throw new ApiError(
      error.message || 'API request failed',
      error.response?.status,
      error.response?.data,
      error.code
    );
  }
}

/**
 * Graceful API request with fallback
 * Returns default value if request fails instead of throwing
 */
export async function apiGetWithFallback<T>(
  url: string,
  fallback: T,
  options?: any
): Promise<T> {
  try {
    return await apiGet<T>(url, options);
  } catch (error: any) {
    logger.warn(`API request failed, using fallback: ${url}`, {
      error: error.message,
      fallback,
    });
    return fallback;
  }
}
